import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { cn } from '../../utils/cn';
import { useThemeColors } from '../../providers/ThemeProvider';

interface DateRangePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onChange: (dates: [Date | null, Date | null]) => void;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  error?: boolean;
  monthsShown?: number;
  showYearDropdown?: boolean;
  showMonthDropdown?: boolean;
  dropdownMode?: 'scroll' | 'select';
  dateFormat?: string;
  className?: string;
  fullWidth?: boolean;
  withPortal?: boolean;
  popperContainer?: () => HTMLElement;
  portalId?: string;
  isClearable?: boolean;
}

export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholderText = 'Select date range...',
  minDate,
  maxDate,
  disabled,
  error,
  monthsShown = 2,
  showYearDropdown = true,
  showMonthDropdown = true,
  dropdownMode = 'select',
  dateFormat = 'MM/dd/yyyy',
  className,
  fullWidth = true,
  withPortal = false,
  popperContainer,
  portalId = 'date-range-picker-portal',
  isClearable = true,
}: DateRangePickerProps) {
  const colors = useThemeColors();
  const [localStartDate, setLocalStartDate] = useState<Date | null>(startDate ?? null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(endDate ?? null);
  const [previousStartDate, setPreviousStartDate] = useState<Date | null>(startDate ?? null);
  const [previousEndDate, setPreviousEndDate] = useState<Date | null>(endDate ?? null);

  useEffect(() => {
    setLocalStartDate(startDate ?? null);
    setLocalEndDate(endDate ?? null);
    setPreviousStartDate(startDate ?? null);
    setPreviousEndDate(endDate ?? null);
  }, [startDate, endDate]);

  // Add CSS to ensure months display side by side and style clear button
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .react-datepicker-side-by-side {
        width: auto !important;
        position: fixed !important;
        z-index: 1000 !important;
      }
      .react-datepicker-side-by-side .react-datepicker__month-container {
        display: inline-block !important;
        margin: 0 4px !important;
        vertical-align: top !important;
        width: auto !important;
      }
      .react-datepicker-side-by-side .react-datepicker__month {
        margin: 0 !important;
      }
      .react-datepicker-popper {
        z-index: 1000 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const hasValue = localStartDate || localEndDate;

  const handleClear = () => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    onChange([null, null]);
  };

  const handleChange = (dates: [Date | null, Date | null]) => {
    console.debug('[DateRangePicker] handleChange called', {
      dates,
      startDate: dates[0],
      endDate: dates[1],
      hasStart: dates[0] != null,
      hasEnd: dates[1] != null
    });

    setLocalStartDate(dates[0]);
    setLocalEndDate(dates[1]);

    if (dates[0] != null && dates[1] != null) {
      console.debug('[DateRangePicker] Both dates selected, calling onChange');
      setPreviousStartDate(dates[0]);
      setPreviousEndDate(dates[1]);
      onChange(dates);
    } else {
      console.debug('[DateRangePicker] Incomplete date range, not calling onChange');
    }
  };

  const handleCalendarClose = () => {
    console.debug('[DateRangePicker] Calendar closing', {
      localStartDate,
      localEndDate,
      hasIncompleteSelection: (localStartDate != null && localEndDate == null) || (localStartDate == null && localEndDate != null)
    });

    if ((localStartDate != null && localEndDate == null) || (localStartDate == null && localEndDate != null)) {
      console.debug('[DateRangePicker] Incomplete selection detected, reverting to previous state', {
        previousStartDate,
        previousEndDate
      });
      setLocalStartDate(previousStartDate);
      setLocalEndDate(previousEndDate);
    }
  };

  return (
    <div className={cn('relative', fullWidth && 'w-full')}>
      <ReactDatePicker
        selectsRange
        startDate={localStartDate}
        endDate={localEndDate}
        onChange={handleChange}
        onCalendarClose={handleCalendarClose}
        placeholderText={placeholderText}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        monthsShown={monthsShown}
        inline={false}
        showMonthYearPicker={false}
        fixedHeight
        showYearDropdown={showYearDropdown}
        showMonthDropdown={showMonthDropdown}
        dropdownMode={dropdownMode}
        dateFormat={dateFormat}
        isClearable={false}
        withPortal={true}
        portalId={portalId}
        popperPlacement="bottom-start"
        popperModifiers={[
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
            },
          },
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        calendarClassName="react-datepicker-side-by-side"
        className={cn(
          'block w-full py-2 border rounded-md shadow-sm text-xs cursor-pointer transition-colors duration-200',
          isClearable ? 'px-2 pr-8' : 'px-2',
          colors.classes.textPrimary,
          colors.classes.bgPrimary,
          'placeholder-gray-400 dark:placeholder-gray-400',
          error
            ? [
                colors.classes.borderError,
                'focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:focus:border-red-400',
              ]
            : [
                colors.classes.borderDefault,
                'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none',
              ],
          disabled &&
            cn(
              colors.classes.bgSecondary,
              'cursor-not-allowed opacity-75',
              colors.classes.borderDisabled,
            ),
          className,
        )}
        wrapperClassName="w-full"
      />

      {/* Custom clear button matching other controls */}
      {hasValue && !disabled && isClearable && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            'absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer',
          )}
          aria-label="Clear date range"
        >
          <FontAwesomeIcon
            icon={faTimes}
            className={cn(
              colors.classes.textSecondary,
              'hover:' + colors.classes.textPrimary,
              'transition-colors',
            )}
            style={{ fontSize: '14px' }}
          />
        </button>
      )}
    </div>
  );
}
